import { useMemo } from 'react';
import { useGetAllCreancesQuery } from 'actions/creances/queries';
import { useUpdateCreanceMutation } from 'actions/creances/mutations';
import { useGetEcrituresManuellesQuery } from 'actions/comptabilite/queries';
import { useGetClientQuery } from 'actions/clients/queries';
import { toast } from 'react-toastify';

const useCreancesData = (dossierId) => {
  const { data: creancesResponse, isLoading, isFetching, refetch } = useGetAllCreancesQuery({
    dossier_id: dossierId,
  });
  const { data: ecrituresResponse, refetch: refetchEcritures } = useGetEcrituresManuellesQuery(
    { dossier_id: dossierId },
    { skip: !dossierId }
  );
  const { data: clientResponse } = useGetClientQuery({ dossier_id: dossierId }, { skip: !dossierId });

  const [updateCreance] = useUpdateCreanceMutation();

  const { updatedCreances, count, totalCreances } = useMemo(() => {
    if (!creancesResponse) {
      return { updatedCreances: [], count: 0, totalCreances: 0 };
    }

    const creancesData = Array.isArray(creancesResponse)
      ? creancesResponse
      : creancesResponse?.data;

    // Map compte_comptable -> nom client
    const clientData = Array.isArray(clientResponse?.data) ? clientResponse.data : (Array.isArray(clientResponse) ? clientResponse : []);
    const clientByCompte = {};
    clientData.forEach((c) => {
      if (c.compte_comptable) clientByCompte[String(c.compte_comptable)] = c;
    });

    // ── Écritures manuelles avec débit 411xxx → créances client ──
    const ecritures = ecrituresResponse?.ecritures || [];
    const ecrituresAsCreances = ecritures
      .filter((e) => {
        if (e.sortie_source || e.entree_source) return false;
        return String(e.code_debit || "").startsWith("411");
      })
      .map((e) => {
        const codeDebit = String(e.code_debit || "");
        const client = clientByCompte[codeDebit];
        const montant = Number(e.debit || 0);
        const cNom = client ? client.nom : (codeDebit.startsWith("411") ? "Client (compte " + codeDebit + ")" : "Non spécifié");
        return {
          id: `MAN-${e.id}`,
          _source: "manuelle",
          intitule: e.libelle_original || e.libelle || "Créance (saisie manuelle)",
          client_details: { nom: cNom },
          client_nom: cNom,
          prix: montant,
          montant_total: montant,
          montant_paye: 0,
          montant_restant: montant,
          reste_a_payer: montant,
          date_transaction: e.date || null,
          updated_at: e.date || null,
          archived: false,
          statut: e.workflow_status === "VALIDE" ? "VALIDE" : "BROUILLON",
          statut_paiement: "NON_PAYE",
        };
      });

    if (creancesData && Array.isArray(creancesData)) {
      const allCreances = [...creancesData, ...ecrituresAsCreances];
      allCreances.sort((a, b) => {
        const dateA = new Date(a.date_transaction || a.created_at || a.updated_at || 0).getTime();
        const dateB = new Date(b.date_transaction || b.created_at || b.updated_at || 0).getTime();
        if (dateB !== dateA) return dateB - dateA;
        const idA = typeof a.id === 'number' ? a.id : parseInt(String(a.id).replace(/\D/g, '') || '0', 10);
        const idB = typeof b.id === 'number' ? b.id : parseInt(String(b.id).replace(/\D/g, '') || '0', 10);
        return idB - idA;
      });
      return {
        updatedCreances: allCreances,
        count: (creancesResponse?.count || creancesData.length) + ecrituresAsCreances.length,
        totalCreances: (creancesResponse?.totalCreances || 0) + ecrituresAsCreances.reduce((s, e) => s + (e.montant_total || 0), 0),
      };
    }

    return { updatedCreances: ecrituresAsCreances, count: ecrituresAsCreances.length, totalCreances: 0 };
  }, [creancesResponse, ecrituresResponse, clientResponse]);

  const handleUpdateCreance = async (creanceId, updateData) => {
    try {
      await updateCreance({ id: creanceId, ...updateData }).unwrap();
      refetch();
    } catch (error) {
      toast.error(
        `Erreur lors de la mise à jour: ${error.data?.error || error.message}`
      );
      console.error(error);
    }
  };

  return {
    updatedCreances,
    handleUpdateCreance,
    count,
    totalCreances,
    isLoading: isLoading || (isFetching && !creancesResponse),
    refetch: () => { refetch(); refetchEcritures(); },
  };
};

export default useCreancesData;

